import React, { useEffect, useState } from "react";
import {
  Privilege,
  getPrivilegesByRoleId,
  getPrivilegesByApplicationId,
} from "@/api/privileges";
import { Role, getRole } from "@/api/roles";
import { Application, getApplication } from "@/api/applications";
import AppRolePrivilegeDetailList from "@/components/privileges/appRolePrivileges/AppRolePrivilegeDetailList";
import AddButton from "@/ui-controls/AddButton";
import NewAppRolePrivilegePanel from "@/components/privileges/appRolePrivileges/NewAppRolePrivilegePanel";
import SearchBox from "@/ui-controls/SearchBox";
import { IconButton, MessageBar, MessageBarType } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { useErrorNotification } from "@/utils/useErrorNotification";

interface Props {
  ApplicationId: number;
  RoleId: number;
  ShowOnlyNewRolePrivilegePanel: boolean;
  onCloseNewAppRolePrivilegePanel?: () => any;
  onSaveNewAppPrivilege?: (privilege: Privilege) => any;
  onAddRolePrivileges?: (privilege: Privilege[]) => any;
  onRoleLoaded?: (name: string) => void;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
  backPath?: string;
}

const AppRolePrivileges = (props: Props) => {
  const navigate = useNavigate();
  const { errorMessage, showError, clearError } = useErrorNotification();
  const [rolePrivileges, setRolePrivileges] = useState<Privilege[]>([]);
  const [applicationPrivileges, setApplicationPrivileges] = useState<
    Privilege[]
  >([]);
  const [unassignedAppPrivileges, setUnassignedAppPrivileges] = useState<
    Privilege[]
  >([]);
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [role, setRole] = useState<Role | undefined>(undefined);
  const [loadingRolePrivileges, setLoadingRolePrivileges] =
    useState<boolean>(false);
  const [loadingAppPrivileges, setLoadingAppPrivileges] =
    useState<boolean>(false);
  const [loadingApplication, setLoadingApplication] = useState<boolean>(false);
  const [loadingRole, setLoadingRole] = useState<boolean>(false);
  const [newAppRolePrivilegePanelOpenned, setNewAppRolePrivilegePanelOpenned] =
    useState<boolean>(props.ShowOnlyNewRolePrivilegePanel);

  const [filteredPrivileges, setFilteredPrivileges] = useState<Privilege[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    if (props.RoleId) {
      getRolePrivileges(props.RoleId);
      getRoleById(props.RoleId);
    }
  }, [props.RoleId]);

  useEffect(() => {
    if (props.ApplicationId) {
      getApplicationPrivileges(props.ApplicationId);
      getApplicationById(props.ApplicationId);
    }
  }, [props.ApplicationId]);

  useEffect(() => {
    getUnassignedAppPrivileges();
  }, [applicationPrivileges, rolePrivileges]);

  const getRolePrivileges = (roleId: number) => {
    setLoadingRolePrivileges(true);
    getPrivilegesByRoleId(roleId)
      .then((result) => {
        setRolePrivileges(result);
        setLoadingRolePrivileges(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingRolePrivileges(false);
      });
  };

  const getApplicationPrivileges = (applicationId: number) => {
    setLoadingAppPrivileges(true);
    getPrivilegesByApplicationId(applicationId)
      .then((result) => {
        setApplicationPrivileges(result);
        setLoadingAppPrivileges(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingAppPrivileges(false);
      });
  };

  const getApplicationById = (applicationId: number) => {
    setLoadingApplication(true);
    getApplication(applicationId)
      .then((result) => {
        setApplication(result);
        setLoadingApplication(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingApplication(false);
      });
  };

  const getRoleById = (roleId: number) => {
    setLoadingRole(true);
    getRole(roleId)
      .then((result) => {
        setRole(result);
        if (props.onRoleLoaded && result?.Name) {
          props.onRoleLoaded(result.Name);
        }
        setLoadingRole(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingRole(false);
      });
  };

  const getUnassignedAppPrivileges = () => {
    const privileges = applicationPrivileges.filter(
      (ap) => !rolePrivileges.find((rp) => rp.Id === ap.Id)
    );
    setUnassignedAppPrivileges(privileges);
  };

  const resfreshRolePrivileges = (privilegeIds: number[]) => {
    const newAssignedPrivileges = applicationPrivileges.filter((ap) =>
      privilegeIds.find((pid) => pid === ap.Id)
    );

    const newRolePrivileges = [...rolePrivileges, ...newAssignedPrivileges];
    if (props.onAddRolePrivileges) props.onAddRolePrivileges(newRolePrivileges);
    setRolePrivileges(newRolePrivileges);
  };

  const handleError = (error: any) => {
    showError(error);
  };

  const handleRefresh = () => {
    setSearchValue("");
    setFilteredPrivileges([]);
    getRolePrivileges(props.RoleId);
  };

  const handlePrivilegeSearch = (searchValue: string) => {
    setSearchValue(searchValue);

    const filteredPrivileges = applicationPrivileges.filter(
      (privilege) =>
        privilege.Name.toLowerCase().includes(searchValue.toLowerCase()) ||
        (privilege.Description &&
          privilege.Description.toLowerCase().includes(
            searchValue.toLowerCase()
          ))
    );

    setFilteredPrivileges(filteredPrivileges);
  };

  useEffect(() => {
    handlePrivilegeSearch(searchValue);
  }, [applicationPrivileges, searchValue]);

  return (
    <>
      {!props.ShowOnlyNewRolePrivilegePanel ? (
        <div className="page-header">
          <div className="breadcrumb-left-section">
            <IconButton
              iconProps={{ iconName: "Back" }}
              title="Back to Roles"
              onClick={() =>
                navigate(
                  props.backPath || `/applications/${props.ApplicationId}/roles`
                )
              }
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: { fontSize: 18, color: "#0078d4" },
                rootPressed: { backgroundColor: "#e1dfdd" },
              }}
            />
            <div>
              {props.breadcrumbTitle && (
                <h2 className="page-breadcrumb-title">
                  {props.breadcrumbTitle}
                </h2>
              )}
              {props.breadcrumbSubtitle && (
                <div style={{ color: "#605e5c", fontSize: "14px" }}>
                  {props.breadcrumbSubtitle}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AddButton
              text="Privilege"
              onClick={() => setNewAppRolePrivilegePanelOpenned(true)}
            />
            <SearchBox placeholder="Search" onSearch={handlePrivilegeSearch} />
            <IconButton
              iconProps={{ iconName: "Refresh" }}
              title="Refresh"
              onClick={handleRefresh}
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: { fontSize: 18, color: "#0078d4" },
                rootPressed: { backgroundColor: "#e1dfdd" },
              }}
            />
          </div>
        </div>
      ) : null}
      <div>
        {role ? (
          <AppRolePrivilegeDetailList
            role={role}
            privilegeList={
              searchValue.length > 0 ? filteredPrivileges : rolePrivileges
            }
            onRemovePrivileges={() => getRolePrivileges(props.RoleId)}
            onDelete={(privilege) => {
              getRolePrivileges(props.RoleId);
              setApplicationPrivileges((prev) =>
                prev.filter((c) => c.Id !== privilege.Id)
              );
            }}
          />
        ) : null}
      </div>
      <NewAppRolePrivilegePanel
        application={application}
        role={role}
        appPrivileges={applicationPrivileges}
        unassignedAppPrivileges={unassignedAppPrivileges}
        isPanelOpenned={newAppRolePrivilegePanelOpenned}
        onPanelClosed={() => {
          setNewAppRolePrivilegePanelOpenned(false);
          if (props.onCloseNewAppRolePrivilegePanel)
            props.onCloseNewAppRolePrivilegePanel();
        }}
        onSaveNewAppPrivilege={(newPrivilege) => {
          // Handle privilege save if needed
        }}
        onAddRolePrivilege={(rolePrivileges) => {
          resfreshRolePrivileges(rolePrivileges.PrivilegeIds);
        }}
      />
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={clearError}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default AppRolePrivileges;
